import React, { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { db, storage } from "@lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  orderBy,
  getDocs as getDocsFromCollection,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { InputFieldProfile } from "../common/InputFieldProfile";
import { InputField } from "../common/InputField";
import { Button } from "../common/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserEdit,
  faCamera,
  faLock,
  faQuestionCircle,
  faEye,
  faEdit,
  faTrash,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";

export const Profile = ({
  labelColor = "text-slate-700",
  showIdentityNumbers = true,
}) => {
  const { user, logout } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    bibName: "",
    email: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    golonganDarah: "",
    sizeJersey: "",
    gender: "",
    negara: "",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    hubunganKontakDarurat: "",
    passportNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState("Edit Profile");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [feedbackData, setFeedbackData] = useState({
    subject: "",
    message: "",
  });
  const [feedbackList, setFeedbackList] = useState([]);
  const [viewFeedback, setViewFeedback] = useState(null);
  const [editFeedback, setEditFeedback] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        if (!user) {
          return;
        }

        if (!user.emailVerified) {
          await logout();
          if (isCancelled) return;
          navigate("/login");
          return;
        }

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (isCancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const fullName = data.namaLengkap || user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          if (!isCancelled) {
            setFormData({
              ...data,
              namaDepan,
              namaBelakang: namaBelakang.join(" "),
            });
          }
        } else {
          const fullName = user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          if (!isCancelled) {
            setFormData({
              namaDepan,
              namaBelakang: namaBelakang.join(" "),
              email: user.email,
              bibName: "",
              telepon: "",
              nomorIdentitas: "",
              tanggalLahir: "",
              golonganDarah: "",
              sizeJersey: "",
              gender: "",
              negara: "",
              provinsi: "",
              kota: "",
              alamat: "",
              kontakDarurat: "",
              namaKontakDarurat: "",
              hubunganKontakDarurat: "",
              passportNumber: "",
            });
          }
          console.log("User data not found in Firestore, using displayName");
        }

        // fetch feedback - guard with isCancelled and user check
        try {
          const feedbackQuery = query(
            collection(db, "feedback"),
            where("uid", "==", user.uid),
            orderBy("createdAt", "desc")
          );
          const feedbackSnapshot = await getDocs(feedbackQuery);
          if (isCancelled) return;
          const feedbackDataArr = feedbackSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          if (!isCancelled) {
            setFeedbackList(feedbackDataArr);
          }
        } catch (feedbackErr) {
          console.error("Failed to fetch feedback:", feedbackErr);
          if (!isCancelled && user) {
            if (
              feedbackErr.code === "failed-precondition" &&
              feedbackErr.message.includes("index")
            ) {
              showNotification(
                "error",
                "Failed to load feedback: Firestore index required. Please contact the admin."
              );
            } else if (
              feedbackErr.code === "permission-denied" ||
              (feedbackErr.message &&
                feedbackErr.message.includes(
                  "Missing or insufficient permissions"
                ))
            ) {
              // suppress noisy permission notification
              console.warn(
                "Permission denied when fetching feedback. Possibly logged out or insufficient rules."
              );
              // optionally logout to keep state consistent
              try {
                await logout();
              } catch (e) {
                console.warn(
                  "Logout failed after feedback permission error",
                  e
                );
              }
              if (!isCancelled) navigate("/login");
            } else {
              showNotification(
                "error",
                "Failed to load feedback. Please try again later."
              );
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
        if (isCancelled) return;

        const isPermissionError =
          err.code === "permission-denied" ||
          (err.message &&
            err.message.includes("Missing or insufficient permissions"));

        if (isPermissionError) {
          // Permission errors are likely due to auth state change or security rules.
          // Avoid showing noisy modal; instead ensure auth state is consistent and redirect to login.
          console.warn(
            "Permission error while fetching profile; ensuring logout and redirecting to login."
          );
          try {
            await logout();
          } catch (e) {
            console.warn("Logout failed while handling permission error", e);
          }
          if (!isCancelled) navigate("/login");
          return;
        }

        // Only show notification if user still present and this is a genuine failure
        if (user) {
          showNotification("error", "Failed to fetch user data.");
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [user, logout, navigate, showNotification]);

  // ---------- Profile submit / update ----------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullName = `${formData.namaDepan} ${formData.namaBelakang}`.trim();
      if (!formData.namaDepan || !formData.namaBelakang) {
        showNotification("error", "First name and last name cannot be empty!");
        setLoading(false);
        return;
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          ...formData,
          namaLengkap: fullName,
          email: user.email,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      const eventsSnapshot = await getDocsFromCollection(
        collection(db, "events")
      );
      let updatedRegistrations = 0;

      const registrationData = {
        namaLengkap: fullName,
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        bibName: formData.bibName,
        email: formData.email,
        telepon: formData.telepon,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        golonganDarah: formData.golonganDarah,
        sizeJersey: formData.sizeJersey,
        gender: formData.gender,
        negara: formData.negara,
        provinsi: formData.provinsi,
        kota: formData.kota,
        alamat: formData.alamat,
        kontakDarurat: formData.kontakDarurat,
        namaKontakDarurat: formData.namaKontakDarurat,
        hubunganKontakDarurat: formData.hubunganKontakDarurat,
        passportNumber: formData.passportNumber,
        updatedAt: Timestamp.now(),
      };

      for (const eventDoc of eventsSnapshot.docs) {
        const { collectionName } = eventDoc.data();
        // Events without a registration collection (e.g. virtual runs, which
        // keep their participants in a subcollection) have nothing to sync.
        if (typeof collectionName !== "string" || !collectionName.trim()) {
          continue;
        }
        const registrationQuery = query(
          collection(db, collectionName.trim()),
          where("uid", "==", user.uid)
        );
        const registrationSnapshot = await getDocs(registrationQuery);

        if (!registrationSnapshot.empty) {
          for (const registrationDoc of registrationSnapshot.docs) {
            await updateDoc(registrationDoc.ref, registrationData);
            updatedRegistrations++;
            console.log(
              `Successfully updated registration in ${collectionName} for orderId=${registrationDoc.id}`
            );
          }
        }
      }

      showNotification("success", "Profile updated successfully!");
    } catch (err) {
      console.error("Failed to save data:", err);
      showNotification("error", "An error occurred while saving data!");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Photo upload ----------
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showNotification("error", "File must be an image (jpg, png, etc.)!");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification("error", "Maximum image size is 5MB!");
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoSubmit = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      showNotification("error", "Please choose an image first!");
      return;
    }

    setLoading(true);
    try {
      const storageRef = ref(storage, `profilePictures/${user.uid}`);
      await uploadBytes(storageRef, photoFile);
      const photoURL = await getDownloadURL(storageRef);

      await updateProfile(user, { photoURL });

      await updateDoc(doc(db, "users", user.uid), { photoURL });

      showNotification("success", "Profile photo updated successfully!");
      setPhotoFile(null);
      setPhotoPreview(null);
    } catch (err) {
      console.error("Failed to upload profile photo:", err);
      showNotification(
        "error",
        "Failed to upload profile photo. Please try again!"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCancel = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // ---------- Password change ----------
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!passwordData.currentPassword) {
        showNotification("error", "Please enter your current password!");
        setLoading(false);
        return;
      }
      if (passwordData.newPassword.length < 6) {
        showNotification(
          "error",
          "New password must be at least 6 characters!"
        );
        setLoading(false);
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showNotification(
          "error",
          "New password and confirmation do not match!"
        );
        setLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwordData.newPassword);

      await updateDoc(doc(db, "users", user.uid), {
        lastPasswordChange: Timestamp.now(),
      });

      showNotification("success", "Password updated successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Failed to update password:", err);
      if (err.code === "auth/wrong-password") {
        showNotification("error", "Current password is incorrect!");
      } else if (err.code === "auth/too-many-requests") {
        showNotification("error", "Too many attempts. Please try again later!");
      } else {
        showNotification(
          "error",
          "Failed to update password. Please try again!"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  // ---------- Feedback ----------
  const handleFeedbackChange = (e) => {
    setFeedbackData({ ...feedbackData, [e.target.name]: e.target.value });
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!feedbackData.subject) {
        showNotification("error", "Subject cannot be empty!");
        setLoading(false);
        return;
      }
      if (!feedbackData.message) {
        showNotification("error", "Message cannot be empty!");
        setLoading(false);
        return;
      }
      if (feedbackData.subject.length > 100) {
        showNotification("error", "Subject maximum is 100 characters!");
        setLoading(false);
        return;
      }
      if (feedbackData.message.length > 1000) {
        showNotification("error", "Message maximum is 1000 characters!");
        setLoading(false);
        return;
      }

      const namaLengkap =
        formData.namaLengkap ||
        user.displayName ||
        `${formData.namaDepan} ${formData.namaBelakang}`.trim() ||
        user.email;
      const feedbackDataToSave = {
        uid: user.uid,
        email: user.email,
        namaLengkap,
        subject: feedbackData.subject,
        message: feedbackData.message,
        createdAt: Timestamp.now(),
        isResolved: false,
      };

      if (formData.telepon) {
        feedbackDataToSave.telepon = formData.telepon;
      }

      const feedbackRef = await addDoc(
        collection(db, "feedback"),
        feedbackDataToSave
      );
      setFeedbackList([
        {
          id: feedbackRef.id,
          ...feedbackDataToSave,
        },
        ...feedbackList,
      ]);

      showNotification("success", "Feedback sent successfully!");
      setFeedbackData({ subject: "", message: "" });
    } catch (err) {
      console.error("Failed to send feedback:", err);
      showNotification("error", "Failed to send feedback. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackData({ subject: "", message: "" });
  };

  const handleFeedbackView = (feedback) => {
    setViewFeedback(feedback);
  };

  const handleFeedbackEdit = (feedback) => {
    setEditFeedback({
      id: feedback.id,
      subject: feedback.subject,
      message: feedback.message,
    });
  };

  const handleEditFeedbackChange = (e) => {
    setEditFeedback({ ...editFeedback, [e.target.name]: e.target.value });
  };

  const handleEditFeedbackSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!editFeedback.subject) {
        showNotification("error", "Subject cannot be empty!");
        setLoading(false);
        return;
      }
      if (!editFeedback.message) {
        showNotification("error", "Message cannot be empty!");
        setLoading(false);
        return;
      }
      if (editFeedback.subject.length > 100) {
        showNotification("error", "Subject maximum is 100 characters!");
        setLoading(false);
        return;
      }
      if (editFeedback.message.length > 1000) {
        showNotification("error", "Message maximum is 1000 characters!");
        setLoading(false);
        return;
      }

      const feedbackRef = doc(db, "feedback", editFeedback.id);
      await updateDoc(feedbackRef, {
        subject: editFeedback.subject,
        message: editFeedback.message,
        updatedAt: Timestamp.now(),
      });

      setFeedbackList(
        feedbackList.map((feedback) =>
          feedback.id === editFeedback.id
            ? {
                ...feedback,
                subject: editFeedback.subject,
                message: editFeedback.message,
                updatedAt: Timestamp.now(),
              }
            : feedback
        )
      );

      showNotification("success", "Feedback updated successfully!");
      setEditFeedback(null);
    } catch (err) {
      console.error("Failed to update feedback:", err);
      showNotification("error", "Failed to update feedback. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditFeedbackCancel = () => {
    setEditFeedback(null);
  };

  const handleFeedbackDelete = async (feedbackId) => {
    showConfirm(
      "Are you sure you want to delete this feedback?",
      async () => {
        setLoading(true);
        try {
          await deleteDoc(doc(db, "feedback", feedbackId));
          setFeedbackList(
            feedbackList.filter((feedback) => feedback.id !== feedbackId)
          );
          showNotification("success", "Feedback deleted successfully!");
        } catch (err) {
          console.error("Failed to delete feedback:", err);
          showNotification(
            "error",
            "Failed to delete feedback. Please try again!"
          );
        } finally {
          setLoading(false);
        }
      },
      "Delete",
      "Cancel"
    );
  };

  const isGoogleLogin = user?.providerData.some(
    (provider) => provider.providerId === "google.com"
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  };

  // MENU DEFINITION
  const menus = [
    { name: "Edit Profile", icon: faUserEdit },
    { name: "Change Profile Photo", icon: faCamera },
    { name: "Change Password", icon: faLock },
    { name: "Send Feedback", icon: faQuestionCircle },
    { name: "Logout", icon: faSignOutAlt, isLogout: true },
  ];

  const renderContent = () => {
    switch (activeMenu) {
      case "Edit Profile":
        return (
          <div className="bg-white rounded-card border border-slate-200 px-4 py-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black text-slate-700">
                Edit Profile
              </h2>
              <button
                type="button"
                onClick={handleLogout}
                className="text-red-500 hover:text-red-600 text-sm font-semibold transition-colors duration-200"
              >
                Logout Account
              </button>
            </div>
            <div className="flex justify-center mb-6">
              <img
                src={
                  user?.photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
                }
                alt="Avatar"
                className="w-24 h-24 rounded-full border-4 border-slate-200 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <p className="text-lg mb-6 text-center text-slate-700">
              Hello,{" "}
              <strong>
                {`${formData.namaDepan} ${formData.namaBelakang}` ||
                  user?.displayName ||
                  user.email}
              </strong>
            </p>
            <form onSubmit={handleSubmit}>
              <InputFieldProfile
                formData={formData}
                handleChange={handleChange}
                isFormDisabled={loading}
                labelColor={labelColor}
                showIdentityNumbers={showIdentityNumbers}
              />
              <div className="flex justify-center mt-6">
                <Button
                  type="submit"
                  color="slate"
                  disabled={loading}
                  className="px-10"
                >
                  {loading ? "Saving..." : "Save Data"}
                </Button>
              </div>
            </form>
          </div>
        );
      case "Change Profile Photo":
        return (
          <div className="bg-white rounded-card border border-slate-200 px-4 py-4">
            <h2 className="text-2xl font-black text-slate-700 mb-6">
              Change Profile Photo
            </h2>
            <div className="flex justify-center mb-6">
              <img
                src={
                  photoPreview ||
                  user?.photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
                }
                alt="Avatar Preview"
                className="w-32 h-32 rounded-full border-4 border-slate-200 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <form onSubmit={handlePhotoSubmit}>
              <InputField
                label="Choose Image"
                name="photo"
                type="file"
                onChange={handlePhotoChange}
                accept="image/*"
                disabled={loading}
                labelColor={labelColor}
              />
              <div className="flex justify-center gap-4 mt-4">
                <Button
                  type="submit"
                  color="slate"
                  disabled={loading}
                >
                  {loading ? "Uploading..." : "Upload"}
                </Button>
                {photoFile && (
                  <Button
                    variant="secondary"
                    onClick={handlePhotoCancel}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>
        );
      case "Change Password":
        return (
          <div className="bg-white rounded-card border border-slate-200 px-4 py-4">
            <h2 className="text-2xl font-black text-slate-700 mb-6">
              Change Password
            </h2>
            {isGoogleLogin ? (
              <p className="text-slate-700">
                Your account uses Google login. Passwords are managed by Google.
              </p>
            ) : (
              <form onSubmit={handlePasswordSubmit}>
                <InputField
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <div className="flex justify-center gap-4 mt-4">
                  <Button
                    type="submit"
                    color="slate"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handlePasswordCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        );
      case "Send Feedback":
        return (
          <div>
            <div className="bg-white rounded-card md:border md:border-slate-200 px-0 md:px-4 py-4 mb-4">
              <h2 className="text-2xl font-black text-slate-700 mb-4">
                Send Feedback
              </h2>
              <p className="text-slate-700 mb-4">
                Send feedback, bug reports, or questions.
              </p>
              <form onSubmit={handleFeedbackSubmit}>
                <InputField
                  label="Subject"
                  name="subject"
                  type="text"
                  value={feedbackData.subject}
                  onChange={handleFeedbackChange}
                  placeholder="Enter feedback subject"
                  maxLength={100}
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="Message"
                  name="message"
                  type="textarea"
                  value={feedbackData.message}
                  onChange={handleFeedbackChange}
                  placeholder="Write your feedback or question"
                  maxLength={1000}
                  required
                  disabled={loading}
                  labelColor={labelColor}
                  className="resize-y h-32"
                />
                <div className="flex justify-center gap-3 mt-4">
                  <Button
                    type="submit"
                    color="slate"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleFeedbackCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-card md:border md:border-slate-200 px-0 md:px-4 py-4">
              <h3 className="text-lg font-semibold text-slate-700 mb-4 py-4 md:px-0">
                Feedback History
              </h3>
              {feedbackList.length === 0 ? (
                <p className="text-slate-700 text-center pb-4">
                  No feedback sent yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-0 md:px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-0 md:px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Message
                        </th>
                        <th className="px-0 md:px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-0 md:px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-0 md:px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbackList.map((feedback) => (
                        <tr
                          key={feedback.id}
                          className="border-b border-gray-200"
                        >
                          <td className="px-0 md:px-4 py-4 text-sm text-slate-700">
                            {feedback.subject}
                          </td>
                          <td className="px-0 md:px-4 py-4 text-sm text-slate-700">
                            {feedback.message.substring(0, 100) +
                              (feedback.message.length > 100 ? "..." : "")}
                          </td>
                          <td className="px-0 md:px-4 py-4 text-sm text-slate-700">
                            {formatDate(feedback.createdAt)}
                          </td>
                          <td className="px-0 md:px-4 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-medium ${
                                feedback.isResolved
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {feedback.isResolved ? "Resolved" : "Pending"}
                            </span>
                          </td>
                          <td className="px-0 md:px-4 py-2 text-sm whitespace-nowrap">
                            <button
                              onClick={() => handleFeedbackView(feedback)}
                              className="text-slate-700 hover:text-slate-900 mr-3"
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              onClick={() => handleFeedbackEdit(feedback)}
                              className="text-slate-700 hover:text-slate-900 mr-3"
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              onClick={() => handleFeedbackDelete(feedback.id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {viewFeedback && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
                <div className="bg-white rounded-card md:border md:border-slate-200 px-0 md:px-4 py-4 w-full max-w-md max-h-[80vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-slate-700 mb-4">
                    Feedback Details
                  </h3>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>
                      <strong>Subject:</strong> {viewFeedback.subject}
                    </p>
                    <p>
                      <strong>Message:</strong> {viewFeedback.message}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatDate(viewFeedback.createdAt)}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {viewFeedback.isResolved ? "Resolved" : "Pending"}
                    </p>
                  </div>
                  <div className="flex justify-center mt-6">
                    <Button
                      variant="secondary"
                      onClick={() => setViewFeedback(null)}
                      fullWidth
                      disabled={loading}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {editFeedback && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
                <div className="bg-white rounded-card md:border md:border-slate-200 px-0 md:px-4 py-4 w-full max-w-md max-h-[80vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-slate-700 mb-4">
                    Edit Feedback
                  </h3>
                  <form onSubmit={handleEditFeedbackSubmit}>
                    <InputField
                      label="Subject"
                      name="subject"
                      type="text"
                      value={editFeedback.subject}
                      onChange={handleEditFeedbackChange}
                      placeholder="Enter feedback subject"
                      maxLength={100}
                      required
                      disabled={loading}
                      labelColor={labelColor}
                    />
                    <InputField
                      label="Message"
                      name="message"
                      type="textarea"
                      value={editFeedback.message}
                      onChange={handleEditFeedbackChange}
                      placeholder="Write your feedback or question"
                      maxLength={1000}
                      required
                      disabled={loading}
                      labelColor={labelColor}
                      className="resize-y h-32"
                    />
                    <div className="flex justify-center gap-3 mt-6">
                      <Button
                        type="submit"
                        color="slate"
                        className="w-full max-w-[120px]"
                        disabled={loading}
                      >
                        {loading ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleEditFeedbackCancel}
                        className="w-full max-w-[120px]"
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const handleLogout = async () => {
    showConfirm(
      "Are you sure you want to log out?",
      async () => {
        setLoading(true);
        try {
          await logout();
          navigate("/login");
        } catch (err) {
          console.error("Failed to logout:", err);
          showNotification("error", "Failed to logout. Please try again!");
        } finally {
          setLoading(false);
        }
      },
      "Yes, Log out",
      "Cancel"
    );
  };

  const handleMenuClick = (menu) => {
    if (menu.isLogout) {
      handleLogout();
    } else {
      setActiveMenu(menu.name);
    }
  };

  return (
    <section className="w-full min-h-screen pt-8 pb-20 bg-white text-slate-700">
      <div className="container mx-auto px-4 max-w-4xl">
        {renderContent()}
      </div>
    </section>
  );
};

export default Profile;
